import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { NamePatternTransformation } from '../Parameters/NamePatternTransformation';
import { AddPrefixParameters } from '../Parameters/AddPrefixParameters';
import { RemovePrefixParameters } from '../Parameters/RemovePrefixParameters';
import { ReplacePrefixParameters } from '../Parameters/ReplacePrefixParameters';
import { FlowLiteralReplaceParameters } from '../Parameters/FlowLiteralReplaceParameters';
import { OverwritePropertyParameters } from '../Parameters/OverwritePropertyParameters';
import { EmailDomainParameters } from '../Parameters/EmailDomainParameters';
import { DivisionMappingParameters } from '../Parameters/DivisionMappingParameters';
import { PropertyBatchUpdateParameters } from '../Parameters/PropertyBatchUpdateParameters';
import { DeleteInvalidDataParameters } from '../Parameters/DeleteInvalidDataParameters';
import { ExcludeResourceParameters } from '../Parameters/ExcludeResourceParameters';
import { ResourceFilterParameters } from '../Parameters/ResourceFilterParameters';
import type { TransformationDefinition } from '../../../types';
import { ui } from '../../../config/uiSizes';
import { getTypeLabelByApiName } from '../../../utils/resourceTypes';
import { isNameTransform } from '../../../utils/transformationPreview';
import TransformationNamePreview from './TransformationNamePreview';

export interface TransformationFormData {
  id: string;
  transformationType: string;
  resourceTypes: string[];
  displayName: string;
  transformation: TransformationDefinition;
  isCollapsed: boolean;
}

interface TransformationFormProps {
  transformation: TransformationFormData;
  index: number;
  sourceOrgName: string;
  targetOrgName: string;
  availableResourceTypes: string[];
  /** Map of resource-type display name -> exported resource names, for the name-change preview. */
  resourcesByType?: Record<string, string[]>;
  transformationTypes: Array<{
    value: string;
    label: string;
    description: string;
  }>;
  onUpdate: (id: string, updates: Partial<TransformationFormData>) => void;
  onRemove: (id: string) => void;
  onToggleCollapse: (id: string) => void;
  /** True when the migration is a Division-to-Division migration */
  isD2D?: boolean;
  /** Source division name (D2D only) */
  sourceDivisionName?: string;
  /** Target division name (D2D only) */
  targetDivisionName?: string;
}

const TransformationForm: React.FC<TransformationFormProps> = ({
  transformation,
  index,
  sourceOrgName,
  targetOrgName,
  availableResourceTypes,
  resourcesByType = {},
  transformationTypes,
  onUpdate,
  onRemove,
  onToggleCollapse,
  isD2D = false,
  sourceDivisionName = '',
  targetDivisionName = ''
}) => {
  const toggleResourceType = useCallback(
    (resourceType: string) => {
      const updatedResourceTypes = transformation.resourceTypes.includes(resourceType)
        ? transformation.resourceTypes.filter((rt) => rt !== resourceType)
        : [...transformation.resourceTypes, resourceType];
      onUpdate(transformation.id, { resourceTypes: updatedResourceTypes });
    },
    [transformation.id, transformation.resourceTypes, onUpdate]
  );

  const handleSelectAllToggle = useCallback(() => {
    const allSelected =
      availableResourceTypes.length > 0 &&
      availableResourceTypes.every((rt) => transformation.resourceTypes.includes(rt));

    const updatedResourceTypes = allSelected ? [] : [...availableResourceTypes];
    onUpdate(transformation.id, { resourceTypes: updatedResourceTypes });
  }, [transformation.id, transformation.resourceTypes, availableResourceTypes, onUpdate]);

  // When the user selects a name-scoping transformation (AddPrefix / RemovePrefix /
  // RenameResource) and no resource types are chosen yet, default the scope to ALL
  // exported types. An empty applyToTypes is treated by the backend as "apply to all",
  // so making it explicit here removes the silent, surprising default and lets the user
  // narrow the scope by unchecking types. FlowLiteralReplace defaults the same way (it
  // only affects flow YAML regardless, but a populated scope avoids an empty selection).
  const handleTypeChange = useCallback(
    (newType: string) => {
      const shouldDefaultScope =
        (isNameTransform(newType) || newType === 'FlowLiteralReplace') &&
        transformation.resourceTypes.length === 0 &&
        availableResourceTypes.length > 0;

      onUpdate(transformation.id, {
        transformationType: newType,
        ...(shouldDefaultScope ? { resourceTypes: [...availableResourceTypes] } : {})
      });
    },
    [transformation.id, transformation.resourceTypes.length, availableResourceTypes, onUpdate]
  );

  const selectedTypeInfo = transformationTypes.find(
    (t) => t.value === transformation.transformationType
  );

  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, height: 0, scale: 0.95 }}
      animate={{ opacity: 1, height: 'auto', scale: 1 }}
      exit={{ opacity: 0, height: 0, scale: 0.95 }}
      transition={{
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
        height: {
          duration: 0.5,
          ease: [0.4, 0, 0.2, 1]
        },
        opacity: {
          duration: 0.3
        },
        scale: {
          duration: 0.4
        }
      }}
      style={{ originY: 0 }}
      className="border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 overflow-hidden"
    >
      <div className="will-change-[height] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
        {/* Collapsed Header */}
        {transformation.isCollapsed ? (
          <div
            className={`${ui.page.sectionPadding} cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors`}
            onClick={() => onToggleCollapse(transformation.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg
                  className={`${ui.svg.sm} text-gray-600 dark:text-gray-400`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <h3 className={`${ui.text.sectionTitle} font-semibold text-gray-800 dark:text-gray-100`}>
                  {transformation.transformationType
                    ? transformationTypes.find((t) => t.value === transformation.transformationType)
                      ?.label || `Transformation ${index + 1}`
                    : `Transformation ${index + 1}`}
                </h3>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(transformation.id);
                }}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 transition text-sm"
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          /* Expanded Form */
          <div className="p-6">
            {/* Header with collapse button */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onToggleCollapse(transformation.id)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition"
                >
                  <svg
                    className={`${ui.svg.sm} text-gray-600 dark:text-gray-400 transform rotate-90`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
                <h3 className={`${ui.text.sectionTitle} font-semibold text-gray-800 dark:text-gray-100`}>
                  Transformation {index + 1}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => onRemove(transformation.id)}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 transition text-sm"
              >
                Remove
              </button>
            </div>

            {/* Transformation Type Dropdown */}
            <div className="mb-4">
              <label
                htmlFor={`transformation-type-${transformation.id}`}
                className={`${ui.text.label} font-semibold text-gray-700 dark:text-gray-300 mb-2`}
              >
                Transformation Type:
              </label>
              <select
                id={`transformation-type-${transformation.id}`}
                value={transformation.transformationType}
                onChange={(e) => handleTypeChange(e.target.value)}
                className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm hover:shadow transition-all`}
              >
                <option value="">Select a transformation type</option>
                {transformationTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  maxHeight: selectedTypeInfo ? '200px' : '0',
                  opacity: selectedTypeInfo ? 1 : 0
                }}
              >
                {selectedTypeInfo && (
                  <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-r-lg">
                    <div className={`flex items-start ${ui.gap.sm}`}>
                      <svg
                        className={`${ui.svg.sm} text-yellow-600  dark:text-yellow-400 flex-shrink-0 mt-0.5`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                      </svg>
                      <p className={`${ui.text.label} text-yellow-800 font-semibold dark:text-yellow-200 leading-relaxed`}>
                        {selectedTypeInfo.description}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Apply to resource types */}
            <div className="mb-6">
              <div className={`${ui.text.label} font-semibold text-gray-700 dark:text-gray-300 mb-2`}>
                Apply To Resource Type(s):
              </div>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-700">
                <div className="flex items-center mb-2">
                  <div className="w-full p-2 rounded-lg bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-800">
                    <div className={`flex items-center ${ui.gap.sm}`}>
                      <input
                        id={`selectAllResources-${transformation.id}`}
                        type="checkbox"
                        checked={
                          availableResourceTypes.length > 0 &&
                          availableResourceTypes.every((rt) =>
                            transformation.resourceTypes.includes(rt)
                          )
                        }
                        onChange={handleSelectAllToggle}
                        className={`${ui.svg.sm} text-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-emerald-400`}
                      />
                      <span className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100`}>
                        Select all that apply
                      </span>
                    </div>
                  </div>
                </div>
                {availableResourceTypes.map((resourceType) => (
                  <div key={resourceType} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`type-${transformation.id}-${resourceType}`}
                      value={resourceType}
                      className={`${ui.svg.sm} ml-2 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
                      checked={transformation.resourceTypes.includes(resourceType)}
                      onChange={() => toggleResourceType(resourceType)}
                    />
                    <label
                      htmlFor={`type-${transformation.id}-${resourceType}`}
                      className={`${ui.text.label} ml-2 p-1 pl-0.5 text-gray-800 dark:text-gray-100`}
                    >
                      {getTypeLabelByApiName(resourceType)}
                    </label>
                  </div>
                ))}
              </div>

              {transformation.resourceTypes.length === 0 ? (
                <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-r-lg">
                  <div className={`flex items-start ${ui.gap.sm}`}>
                    <svg
                      className={`${ui.svg.sm} text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className={`${ui.text.label} text-red-800 font-semibold dark:text-red-200 leading-relaxed`}>
                      No resource types selected. This transformation cannot run until you
                      select at least one type above.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-r-lg">
                  <div className={`flex items-start ${ui.gap.sm}`}>
                    <svg
                      className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                    <p className={`${ui.text.label} text-yellow-800 font-semibold dark:text-yellow-200 leading-relaxed`}>
                      Transform will only be applied to the resource type(s) selected above.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Parameters Section - Conditional rendering based on transformation type */}
            {transformation.transformationType && (
              <div className="mb-6">
                {transformation.transformationType === 'RenameResource' && (
                  <NamePatternTransformation
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'AddPrefix' && (
                  <AddPrefixParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    resourceTypes={transformation.resourceTypes}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'RemovePrefix' && (
                  <RemovePrefixParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'ReplacePrefix' && (
                  <ReplacePrefixParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'FlowLiteralReplace' && (
                  <FlowLiteralReplaceParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'OverwriteProperty' && (
                  <OverwritePropertyParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'EmailDomain' && (
                  <EmailDomainParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'DivisionMapping' && (
                  <DivisionMappingParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    isD2D={isD2D}
                    sourceOrgKey={sourceOrgName}
                    targetOrgKey={targetOrgName}
                    sourceDivisionName={sourceDivisionName}
                    targetDivisionName={targetDivisionName}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'PropertyBatchUpdate' && (
                  <PropertyBatchUpdateParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'DeleteInvalidData' && (
                  <DeleteInvalidDataParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'ExcludeResource' && (
                  <ExcludeResourceParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}

                {transformation.transformationType === 'ResourceFilter' && (
                  <ResourceFilterParameters
                    {...(transformation.transformation.parameters && {
                      parameters: transformation.transformation.parameters
                    })}
                    onParametersChange={(params) =>
                      onUpdate(transformation.id, {
                        transformation: {
                          ...transformation.transformation,
                          parameters: params
                        }
                      })
                    }
                  />
                )}
              </div>
            )}

            {/* Pre-commit preview of the name changes this transform will make */}
            {isNameTransform(transformation.transformationType) && (
              <div className="mb-6">
                <TransformationNamePreview
                  transformationType={transformation.transformationType}
                  {...(transformation.transformation.parameters && {
                    parameters: transformation.transformation.parameters
                  })}
                  resourceTypes={transformation.resourceTypes}
                  resourcesByType={resourcesByType}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TransformationForm;
